import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { RewardsService } from 'src/app/services/rewards/rewards.service';
import { UtilsService } from 'src/app/services/utils/utils.service';

@Component({
  selector: 'app-rewards-current-status',
  templateUrl: './rewards-current-status.component.html',
  styleUrls: ['./rewards-current-status.component.scss'],
})
export class RewardsCurrentStatusComponent implements OnInit {
  @Output() onClickRedeem: EventEmitter<any> = new EventEmitter();
  @Output() onClickPointsChange: EventEmitter<any> = new EventEmitter();
  @Input() points = 0;
  @Input() minimumPoints = 0; // minimum points required
  @Input('isTierInfoHidden') isTierInfoHidden: boolean = false;
  // slider values
  @Input() isSliderHidden: boolean = false;
  @Input() pointsPercentage = 0
  // for multiple rates
  @Input() isMultipleRates: boolean = false;
  @Input() convertionRate: any = []
  // for single rates
  @Input() userRate: number = 0;
  // buttons and labels
  @Input() convertValueLabel: string = "Cash value";
  @Input() buttonText: string = "redeem now";

  public canRedeemPoints: boolean = false;
  public cashValue: number = 0;
  public rewardCardIcon: string = '';
  public loyaltyInfo: LoyaltyPoints = {
    totalRewards: 0,
    pointsToNextLevel: 0,
    totalRewardsMonth: 0,
    currentTier: 0,
    nextTierName: "",
    currentTierName: "",
    nextLevelRateBetterPercent: 0,
    currentProgress: 0
  };
  selectedRate;

  redeemForm = new FormGroup({
    points: new FormControl('', [Validators.required]),
  });

  constructor(private utils: UtilsService, private rewardsService: RewardsService) { }
  ngOnInit() { }

  ngOnChanges() {
    this.initAllData();
  }

  async initAllData() {

    try {
      const results: any = await this.rewardsService.callGetLoyaltyPoints();
      this.loyaltyInfo = results
      this.setMinimumCashValueToPoints();
    } catch (error) {
      console.log("error ", error)
    }
  }

  private setMinimumCashValueToPoints() {
    const pointsControl = this.redeemForm.get("points");
    if (pointsControl) {
      pointsControl.setValidators([
        Validators.required,
        Validators.min(this.minimumPoints),
        Validators.max(this.loyaltyInfo?.totalRewards),
        Validators.pattern('^[0-9]+(\\.[0-9]+)?$')
      ]);
      pointsControl.markAsDirty();
      pointsControl.markAllAsTouched();
    }

    this.canRedeemPoints = this.loyaltyInfo?.totalRewards  >= this.minimumPoints;

    if (!this.points && this.canRedeemPoints) {
      setTimeout(() => this.setPointsValue(this.loyaltyInfo?.totalRewards), 10);
    } else {
      this.calculatePercentage(this.points);
      this.calculateCashValue();
    }
    this.setRewardsIcon();
  }

  adjustFontSize(value: number): number {
    const length = value?.toString().length;
    if (length > 15) {
      return 10;
    } else if (length > 10) {
      return 13;
    } else {
      return 16;
    }
  }

  private setRewardsIcon() {
    const level = this.loyaltyInfo?.currentTier || 1;
    const imgName = `rewards_icon_${level}.png`;
    this.rewardCardIcon = this.utils.getCDNAsset(`rewardcardicons/${imgName}`);
  }

  sliderChange(value) {
    this.calculatePoints(value ?? 0);
  }

  private calculatePoints(percentage) {
    let pointValue = ((percentage) / 100) * (this.loyaltyInfo?.totalRewards - this.minimumPoints)
    pointValue = parseFloat(pointValue.toFixed(2));
    this.setPointsValue(pointValue + this.minimumPoints);
  }

  private calculatePercentage(points) {
    const pointValue = isNaN(Number(points)) || Number(points) < 0 ? 0 : Number(points);
    if (pointValue <= this.minimumPoints) {
      this.setSliderZero();
    } else {
      this.pointsPercentage = ((points - this.minimumPoints) / (this.loyaltyInfo?.totalRewards - this.minimumPoints)) * 100;
    }
    this.setPointsValue(pointValue);
  }

  private setSliderZero() {
    this.pointsPercentage = 1;
    setTimeout(() => {
      this.pointsPercentage = 0;
    }, 10);
  }

  private setPointsValue(pointValue) {
    pointValue = pointValue == 0 && (Number(pointValue) < 0) ? this.points : pointValue;
    const val = Math.floor(pointValue);
    this.redeemForm.get("points").setValue(`${val}`);
    this.points = val;
    if (this.isMultipleRates) {
      this.calculateMultiRateCashValue();
    } else {
      this.calculateCashValue();
    }
    this.onClickPointsChange.emit(val);
  }

  private calculateCashValue() {
    this.cashValue = this.calculateRateValue(this.userRate);
  }

  private calculateMultiRateCashValue() {
    this.convertionRate = this.convertionRate.map((item: any) => {
      const cashVal = this.calculateRateValue(item.rate);
      return {
        ...item,
        fontSize:this.setFontSize(cashVal),
        convertedValue: cashVal
      }
    });
  }

  private setFontSize(value){
    const length = value?.toFixed(2)?.toString().length;
      if (length > 13) {
        return 12
      } else if (length > 8) {
        return 14
      } else if (length > 4) {
        return 20;
      } else {
        return 30;
      }
  }

  public calculateRateValue(rate) {
    return this.points * rate;
  }

  onInputChange(e) {
    const value = Math.floor(e.target.value);
    this.calculatePercentage(value);
  }

  clickConvert(rate) {
    this.calculatePercentage(this.points);
    this.cashValue = rate?.convertedValue;
    this.selectedRate = rate?.bucket;
    this.clickRedeem();
  }

  clickRedeem() {
    this.onClickRedeem.emit({ cashValue: this.cashValue, bucket: this.selectedRate });
  }

}

export interface LoyaltyPoints {
  totalRewards: number;
  pointsToNextLevel: number;
  totalRewardsMonth: number;
  currentTier: number;
  nextTierName: string;
  currentTierName: string;
  nextLevelRateBetterPercent: number;
  currentProgress: number;
}
